import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControlLabel, Paper, Switch, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { AuthService } from '../../../services/auth.service';
import { PortalAdminService, PortalSettings as Settings } from '../../../services/portalAdmin.service';

// ---- P1-B customer portal: /sales/portal-settings ----
const PortalSettings: React.FC = () => {
  const institutionId = AuthService.getSelectedInstitution() as string;
  const toast = useToast();
  const canPortal = usePermission(PERMISSIONS.PORTAL_MANAGE);
  const canSettings = usePermission(PERMISSIONS.SALES_SETTINGS_MANAGE);
  const canManage = canPortal && canSettings;
  const [loaded, setLoaded] = useState<Settings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [uploadsEnabled, setUploadsEnabled] = useState(true);
  const [minutes, setMinutes] = useState('60');
  const [saving, setSaving] = useState(false);

  const apply = (s: Settings) => {
    setLoaded(s); setEnabled(s.enabled); setEmailEnabled(s.emailEnabled); setUploadsEnabled(s.uploadsEnabled); setMinutes(String(s.sessionMinutes));
  };
  const load = useCallback(async () => {
    try { apply(await PortalAdminService.getSettings(institutionId)); }
    catch (e) { toast.showApiError(e, 'Failed to load the portal settings'); }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const mins = Number(minutes);
  const minsValid = Number.isInteger(mins) && mins >= 15 && mins <= 480;
  const save = async () => {
    try {
      setSaving(true);
      apply(await PortalAdminService.saveSettings(institutionId, { enabled, emailEnabled, uploadsEnabled, sessionMinutes: mins }));
      toast.showSuccess('Portal settings saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save the portal settings');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  return (
    <Box sx={{ p: 3, maxWidth: 720 }} data-help-page="sales.portalSettings.page">
      <Typography variant="h5" gutterBottom>Customer Portal</Typography>
      {!loaded.serverConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }}>The customer portal is not configured on this server, so it stays off whatever is set here.</Alert>
      )}
      <Paper sx={{ p: 2 }} data-help-section="sales.portalSettings.section.main">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel data-help="sales.portalSettings.enabled" control={<Switch checked={enabled} disabled={!canManage} onChange={e => setEnabled(e.target.checked)} />} label="Enable customer portal" />
          <FormControlLabel data-help="sales.portalSettings.uploads" control={<Switch checked={uploadsEnabled} disabled={!canManage} onChange={e => setUploadsEnabled(e.target.checked)} />} label="Allow document uploads" />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, my: 1 }}>
            <TextField data-help="sales.portalSettings.sessionMinutes" label="Session minutes (15-480)" type="number" size="small" value={minutes} disabled={!canManage}
              error={!minsValid} helperText={!minsValid ? 'Enter a whole number from 15 to 480' : undefined}
              sx={{ flex: '1 1 200px', maxWidth: 260 }} onChange={e => setMinutes(e.target.value)} />
          </Box>
          <FormControlLabel data-help="sales.portalSettings.email" control={<Switch checked={emailEnabled} disabled={!canManage} onChange={e => setEmailEnabled(e.target.checked)} />} label="Send portal e-mails" />
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            E-mails go to real customer addresses. Leave off on staging and demo institutions.
          </Typography>
        </Box>
        {canManage && <Button data-help="sales.portalSettings.save" variant="contained" sx={{ mt: 2 }} disabled={saving || !minsValid} onClick={save}>Save</Button>}
      </Paper>
    </Box>
  );
};

export default PortalSettings;
// ---- end P1-B ----
